import React, { useState } from 'react';
import { 
  X, 
  PlayCircle, 
  BookOpen, 
  CheckCircle, 
  Clock,
  Users,
  FileText,
  Settings,
  Search,
  ChevronRight,
  Video,
  Download,
  ExternalLink
} from 'lucide-react';

const TutorialModal = ({ isOpen, onClose, currentUser }) => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [completedVideos, setCompletedVideos] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  if (!isOpen) return null;

  // Tutorial sections with videos and content
  const tutorialSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: PlayCircle,
      gradient: 'from-blue-600 to-cyan-600',
      videos: [
        {
          id: 'intro',
          title: 'Platform Introduction',
          duration: '5:30',
          description: 'Overview of the Candidate Management Platform and its key features',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', // Replace with your actual video
          thumbnail: 'https://via.placeholder.com/640x360/3b82f6/ffffff?text=Platform+Introduction'
        },
        {
          id: 'navigation',
          title: 'Dashboard Navigation',
          duration: '4:15',
          description: 'Learn how to navigate through different sections and features',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/3b82f6/ffffff?text=Navigation+Guide'
        },
        {
          id: 'user-roles',
          title: 'Understanding User Roles',
          duration: '3:45',
          description: 'Different user roles and their permissions in the system',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/3b82f6/ffffff?text=User+Roles'
        }
      ]
    },
    {
      id: 'candidate-management',
      title: 'Candidate Management',
      icon: Users,
      gradient: 'from-purple-600 to-pink-600',
      videos: [
        {
          id: 'add-candidate',
          title: 'Adding New Candidates',
          duration: '6:20',
          description: 'Step-by-step guide to adding candidates to the system',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/a855f7/ffffff?text=Add+Candidates'
        },
        {
          id: 'edit-candidate',
          title: 'Editing Candidate Information',
          duration: '4:50',
          description: 'How to update and manage candidate details',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/a855f7/ffffff?text=Edit+Candidates'
        },
        {
          id: 'candidate-status',
          title: 'Managing Candidate Status',
          duration: '5:10',
          description: 'Track and update candidate status throughout the process',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/a855f7/ffffff?text=Candidate+Status'
        }
      ]
    },
    {
      id: 'import-export',
      title: 'Import & Export Data',
      icon: FileText,
      gradient: 'from-emerald-600 to-teal-600',
      videos: [
        {
          id: 'csv-import',
          title: 'Importing CSV Files',
          duration: '7:15',
          description: 'Learn how to bulk import candidate data using CSV files',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/10b981/ffffff?text=CSV+Import'
        },
        {
          id: 'export-data',
          title: 'Exporting Reports',
          duration: '5:40',
          description: 'Export candidate data and generate reports',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/10b981/ffffff?text=Export+Data'
        },
        {
          id: 'google-sheets-sync',
          title: 'Google Sheets Integration',
          duration: '8:30',
          description: 'Sync your data with Google Sheets automatically',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/10b981/ffffff?text=Google+Sheets+Sync'
        }
      ]
    },
    {
      id: 'rolloff-management',
      title: 'Roll-off Management',
      icon: Clock,
      gradient: 'from-red-600 to-orange-600',
      videos: [
        {
          id: 'rolloff-tracking',
          title: 'Tracking Roll-off Candidates',
          duration: '6:00',
          description: 'Monitor candidates approaching their end dates',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/ef4444/ffffff?text=Roll-off+Tracking'
        },
        {
          id: 'extension-requests',
          title: 'Managing Extension Requests',
          duration: '5:25',
          description: 'Process and approve contract extension requests',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/ef4444/ffffff?text=Extension+Requests'
        }
      ]
    },
    {
      id: 'admin-features',
      title: 'Admin Features',
      icon: Settings,
      gradient: 'from-indigo-600 to-purple-600',
      restricted: true,
      videos: [
        {
          id: 'user-management',
          title: 'User Management',
          duration: '7:45',
          description: 'Add, edit, and manage user accounts and permissions',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/6366f1/ffffff?text=User+Management'
        },
        {
          id: 'activity-logs',
          title: 'Viewing Activity Logs',
          duration: '4:30',
          description: 'Monitor system activity and user actions',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/6366f1/ffffff?text=Activity+Logs'
        },
        {
          id: 'system-settings',
          title: 'System Configuration',
          duration: '9:15',
          description: 'Configure system settings and preferences',
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://via.placeholder.com/640x360/6366f1/ffffff?text=System+Settings'
        }
      ]
    }
  ];

  // Filter sections based on user role
  const availableSections = tutorialSections.filter(section => {
    if (section.restricted) {
      return currentUser?.role === 'Admin' || currentUser?.role === 'Team Lead';
    }
    return true;
  });

  // Filter videos based on search
  const getFilteredVideos = () => {
    if (!searchQuery) {
      const currentActiveSection = availableSections.find(s => s.id === activeSection);
      return currentActiveSection?.videos || [];
    }

    const allVideos = availableSections.flatMap(section => 
      section.videos.map(video => ({ ...video, sectionId: section.id, sectionTitle: section.title }))
    );

    return allVideos.filter(video => 
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      video.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const toggleVideoComplete = (videoId) => {
    setCompletedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const currentSection = availableSections.find(s => s.id === activeSection);
  const filteredVideos = getFilteredVideos();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="relative w-full max-w-7xl max-h-[95vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="relative bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 p-6 sm:p-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all transform hover:scale-110"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center">
              <Video className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Tutorial Center
              </h2>
              <p className="text-white/90 text-sm sm:text-base mt-1">
                Learn how to use the Candidate Management Platform
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/60" />
            <input
              type="text"
              placeholder="Search tutorials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/20 backdrop-blur-xl border-2 border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:border-white/50 transition-all"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* Sidebar - Category Navigation */}
          {!searchQuery && (
            <div className="w-64 bg-slate-50 border-r border-slate-200 overflow-y-auto p-4">
              <div className="space-y-2">
                {availableSections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  const completedCount = section.videos.filter(v => 
                    completedVideos.includes(v.id)
                  ).length;

                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all text-left ${
                        isActive
                          ? `bg-gradient-to-r ${section.gradient} text-white shadow-lg`
                          : 'text-slate-700 hover:bg-white hover:shadow-md'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">
                          {section.title}
                        </div>
                        <div className={`text-xs mt-0.5 ${
                          isActive ? 'text-white/80' : 'text-slate-500'
                        }`}>
                          {completedCount}/{section.videos.length} completed
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`} />
                    </button>
                  );
                })}
              </div>

              {/* Progress Summary */}
              <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-sm text-slate-900">
                    Your Progress
                  </span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {completedVideos.length}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  videos completed
                </div>
              </div>
            </div>
          )}

          {/* Main Content - Video Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {searchQuery && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  Search Results for "{searchQuery}"
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Found {filteredVideos.length} tutorial{filteredVideos.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {!searchQuery && currentSection && (
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900">
                  {currentSection.title}
                </h3>
                <p className="text-slate-600 mt-2">
                  {currentSection.videos.length} tutorial{currentSection.videos.length !== 1 ? 's' : ''} available
                </p>
              </div>
            )}

            {filteredVideos.length === 0 ? (
              <div className="text-center py-12">
                <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tutorials found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredVideos.map((video) => {
                  const isCompleted = completedVideos.includes(video.id);

                  return (
                    <div
                      key={video.id}
                      className="group bg-white rounded-2xl border-2 border-slate-200 overflow-hidden hover:border-orange-300 hover:shadow-xl transition-all"
                    >
                      {/* Video Thumbnail */}
                      <div className="relative aspect-video bg-slate-900 overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                        
                        {/* Play Button Overlay */}
                        <button
                          className="absolute inset-0 flex items-center justify-center"
                          onClick={() => {
                            // Open video in modal or new tab
                            window.open(video.videoUrl, '_blank');
                          }}
                        >
                          <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-xl">
                            <PlayCircle className="w-10 h-10 text-orange-600" />
                          </div>
                        </button>

                        {/* Duration Badge */}
                        <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/80 backdrop-blur-sm rounded-lg">
                          <span className="text-xs font-semibold text-white flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{video.duration}</span>
                          </span>
                        </div>

                        {/* Completed Badge */}
                        {isCompleted && (
                          <div className="absolute top-3 left-3 px-3 py-1 bg-green-500 rounded-lg shadow-lg">
                            <span className="text-xs font-bold text-white flex items-center space-x-1">
                              <CheckCircle className="w-3 h-3" />
                              <span>Completed</span>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Video Info */}
                      <div className="p-5">
                        <h4 className="font-bold text-slate-900 text-lg mb-2 group-hover:text-orange-600 transition-colors">
                          {video.title}
                        </h4>
                        <p className="text-sm text-slate-600 mb-4">
                          {video.description}
                        </p>

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => toggleVideoComplete(video.id)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                              isCompleted
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>{isCompleted ? 'Completed' : 'Mark Complete'}</span>
                          </button>

                          <button
                            onClick={() => window.open(video.videoUrl, '_blank')}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                            title="Open in new tab"
                          >
                            <ExternalLink className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center justify-between text-sm">
            <div className="text-slate-600">
              Need more help? Contact <a href="mailto:support@example.com" className="text-orange-600 hover:text-orange-700 font-medium">support@example.com</a>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl font-semibold hover:from-orange-700 hover:to-red-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;